import type { graphTypeEnum } from '@/features/analysis/_models/project';
import {
  escapeHtml,
  tooltipText,
  truncateLabel,
} from '@/features/analysis/_utils';
import { t } from '@/i18n';
import {
  type AmountUnit,
  AmountUnitLabelModifier,
  formatValueByUnit,
} from '@/utils';
import chroma from 'chroma-js';
import type {
  Options,
  PointLabelObject,
  SeriesOptionsRegistry,
} from 'highcharts';
import type { z } from 'zod';
import { groupInOrder } from '../../_utils/group-in-order';
import type { KsAnalysisRow } from '../_models/ks-analysis';
// 円/ツリーの既定表示年度ロジックは pure util 側へ移設（初期ソート解決と共有）。既存 import 互換のため再エクスポート。
export { getKsDefaultGraphYear } from './resolve-ks-ordering';

const NOT_SET_GROUP_KEY = '__not_set__';
const TOTAL_GROUP_KEY = '__total__';

const chromaColorSet3 = [
  '#8dd3c7',
  '#ffffb3',
  '#bebada',
  '#fb8072',
  '#80b1d3',
  '#fdb462',
  '#b3de69',
  '#fccde5',
  '#d9d9d9',
  '#bc80bd',
  '#ccebc5',
  '#ffed6f',
];

const getColorPalette = (dataLength: number) =>
  chroma.scale(chromaColorSet3).colors(dataLength);

type GroupedKsData = [string, KsAnalysisRow[]][];

/**
 * KS 分析データを `analysisAxis.id` でグルーピングする。
 * 軸 = null の場合は単一の合計グループにまとめる。
 * 軸 = project / fund-owner などで BE から id が null で返るケースは name をキーにフォールバックする。
 *
 * BE が金額順でソート済みの配列を返すため、`groupInOrder` で出現順を保持してグルーピングする。
 * `Object.keys`/`Object.entries` を使うと名称が数字のみのキーが昇順に並べ替わり、BE のソート順が
 * 失われてしまうため使用しない。
 */
export const groupKsDataByAxis = (data: KsAnalysisRow[]): GroupedKsData => {
  return groupInOrder(data, (row) => {
    if (row.analysisAxis.id === null && row.analysisAxis.name === null) {
      return TOTAL_GROUP_KEY;
    }
    return row.analysisAxis.id ?? row.analysisAxis.name ?? NOT_SET_GROUP_KEY;
  });
};

const getDisplayName = (rows: KsAnalysisRow[], key: string): string => {
  if (key === TOTAL_GROUP_KEY) {
    return t('total-amount');
  }
  const name = rows.at(0)?.analysisAxis.name;
  return name?.toString() ?? t('not-set');
};

type KsSeries = {
  name: string;
  cursor: 'default';
  color: string;
  custom: { id: string | null };
  data: { x: number; y: number | null }[];
};

/**
 * KS 用シリーズデータを生成する。
 * RS と異なり、KS は「その他集計を行わずすべての項目を表示する」要件のため、
 * クリックハンドラは設定せず固定的に表示するだけにする。
 */
export const getKsSeriesData = (data: KsAnalysisRow[]): KsSeries[] => {
  const grouped = groupKsDataByAxis(data);
  const palette = getColorPalette(grouped.length);
  return grouped.map(([key, rows], idx) => {
    return {
      name: getDisplayName(rows, key),
      cursor: 'default' as const,
      color: palette[idx] ?? '#cccccc',
      custom: { id: rows.at(0)?.analysisAxis.id ?? null },
      data: rows.map((row) => ({ x: row.fiscalYear, y: row.total })),
    };
  });
};

type GetKsGraphOptionsArgs = {
  graphType: z.infer<typeof graphTypeEnum>;
  seriesData: KsSeries[];
  selectedYear: number | null;
  unit: AmountUnit;
  /**
   * チャートクリック時のドリルダウンハンドラ。
   * `null` を渡すと、クリックハンドラは設定されない（ドリルダウン無効状態）。
   */
  onPointClick?: ((value: { id: string }) => void) | null;
  /** 現在ドリルダウン中の id。クリックしたものと同一であればハンドラを実行しない */
  currentId?: string | null;
};

type ColumnOptions = SeriesOptionsRegistry['SeriesColumnOptions'];

/**
 * グラフタイプ別の `Highcharts.Options.series` / `plotOptions` を構築する。
 * 共通の chart / title / xAxis / yAxis / tooltip 設定は `getKsLineGraphOptions` で被せる。
 */
export const getKsGraphOptions = ({
  graphType,
  seriesData,
  selectedYear,
  unit,
  onPointClick,
  currentId,
}: GetKsGraphOptionsArgs): Options => {
  const handleSeriesClick = onPointClick
    ? function (this: { options: { custom?: { id?: string } } }) {
        const id = this.options.custom?.id;
        if (id && id !== currentId) {
          onPointClick({ id });
        }
      }
    : undefined;
  const handlePointEventClick = onPointClick
    ? function (
        this: unknown,
        events: { point: { options: { custom?: { id?: string } } } },
      ) {
        const id = events.point.options.custom?.id;
        if (id && id !== currentId) {
          onPointClick({ id });
        }
      }
    : undefined;
  // 円グラフ・ツリーマップの点は、凡例(seriesData)と同じ並び順・色になるよう
  // ソート済みの seriesData から構築する（groupKsDataByAxis の BE 出現順で組み直さない）。
  const formattedData = seriesData.map((series) => ({
    name: series.name,
    y: series.data.find((point) => point.x === selectedYear)?.y ?? null,
    color: series.color,
    custom: series.custom,
  }));

  const seriesCursor = onPointClick ? 'pointer' : 'default';
  switch (graphType) {
    case 'stackedVerticalBar':
      return {
        series: seriesData.map<ColumnOptions>((item) => ({
          ...item,
          cursor: seriesCursor,
          type: 'column',
          visible: true,
          ...(handleSeriesClick && {
            events: { click: handleSeriesClick },
          }),
        })),
        plotOptions: {
          column: {
            stacking: 'normal',
            groupPadding: 0,
            groupZPadding: 0,
            borderWidth: 0,
          },
        },
      };
    case 'treeMap':
      return {
        series: [
          {
            type: 'treemap',
            data: formattedData.map((item) => ({
              ...item,
              value: item.y,
            })),
            visible: true,
            ...(handlePointEventClick && {
              events: { click: handlePointEventClick },
            }),
          },
        ],
        plotOptions: {
          treemap: {
            borderWidth: 0,
          },
        },
      };
    case 'pieChart':
      return {
        series: [
          {
            name: 'Pie',
            type: 'pie',
            cursor: seriesCursor,
            borderWidth: 0,
            ...(handlePointEventClick && {
              events: { click: handlePointEventClick },
            }),
            data: formattedData.map((item) => ({
              ...item,
              dataLabels: {
                formatter: function (this: PointLabelObject) {
                  const fullName = this.key ?? '';
                  return `<div style="display: flex; flex-direction: column; font-size: 12px">
                        <span title="${escapeHtml(fullName)}">${escapeHtml(truncateLabel(fullName))}</span>
                        <span>${formatValueByUnit(item.y ?? 0, {
                          operation: 'round',
                          unit,
                          showUnitLabel: true,
                        })} (${this.percentage.toFixed(2)}%)</span>
                      </div>`;
                },
                useHTML: true,
                // 円グラフは凡例数が多くなり得るため、ラベル同士が重なっても表示する
                allowOverlap: true,
              },
            })),
            visible: true,
          },
        ],
      };
    default:
      return {};
  }
};

type GetKsLineGraphOptionsArgs = {
  graphOptions: Options;
  graphType: z.infer<typeof graphTypeEnum>;
  unit: AmountUnit;
  /** 検索フォームで指定されている事業年度（= 当年度）。xAxis title / カテゴリラベルに使う */
  currentFiscalYear: number;
};

/**
 * 年度を「前年度(YYYY)」「当年度(YYYY)見込み」など意味付きのラベルにする。
 */
export const getKsFiscalYearLabel = (
  year: number,
  currentFiscalYear: number,
): string => {
  if (year === currentFiscalYear) {
    return t('ks-analysis-current-fiscal-year-label', { year });
  }
  if (year < currentFiscalYear) {
    return t('ks-analysis-previous-fiscal-year-label', { year });
  }
  return year.toString();
};

/**
 * KS 用 Highcharts オプションのベース。RS の `getLineGraphOptions` と同様だが、
 * クリックでドリルダウンしない要件のため、ハンドラ系の指定を持たない。
 */
export const getKsLineGraphOptions = ({
  graphOptions,
  graphType,
  unit,
  currentFiscalYear,
}: GetKsLineGraphOptionsArgs): Options => {
  return {
    chart: { backgroundColor: 'transparent' },
    title: { text: '' },
    credits: { enabled: false },
    exporting: { enabled: false },
    tooltip: {
      // 円グラフ・ツリーマップは系列名ではなく点ごとの名称が意味を持つためヘッダ（年度）を出さない
      headerFormat:
        graphType === 'treeMap' || graphType === 'pieChart'
          ? ''
          : '<b>{point.x}</b><br/>',
      useHTML: true,
      // 円グラフはデータラベルを省略表示しているため、ホバー時にフル名称を出す。
      // ネイティブ title 属性と異なり Highcharts のツールチップは即時表示できる。
      enabled: true,
      backgroundColor: '#ffffff',
      // useHTML のツールチップが SVG 内に描画されると背後のデータラベルが透けるため、
      // チャート外の独立 DOM（最前面）に描画して白背景を確実に乗せる。
      outside: true,
      style: { fontSize: '14px' },
      pointFormatter: function (this) {
        const label =
          graphType === 'treeMap' || graphType === 'pieChart'
            ? this.name
            : this.series.name;
        return `<div>
          <span style="color: ${this.color};">●</span>
          <span>${escapeHtml(label ?? '')}: </span>
          <b>${tooltipText(unit, this.y)}</b>
        </div>`;
      },
    },
    legend: { enabled: false },
    xAxis: {
      title: {
        text: t('ks-analysis-fiscal-year-axis-title'),
      },
      tickInterval: 1,
      visible: graphType !== 'pieChart',
      // series データの x は実年度 (例: 2024 / 2025) なので、Highcharts の `categories`
      // を使うと x の整数値とインデックスが噛み合わずラベルが反映されない。
      // ラベルは `formatter` で年度値を直接「前年度(YYYY) / 当年度(YYYY)見込み」に整形する。
      labels: {
        style: { fontSize: '14px', letterSpacing: '-1px' },
        formatter: function (this) {
          const year = Number(this.value);
          if (Number.isInteger(year) && year > 0) {
            return getKsFiscalYearLabel(year, currentFiscalYear);
          }
          return String(this.value);
        },
      },
    },
    yAxis: {
      title: {
        text: t('amount-label-with-unit', {
          unit: AmountUnitLabelModifier[unit],
        }),
      },
      visible: graphType !== 'pieChart',
      labels: {
        formatter: function (this) {
          return formatValueByUnit(Number(this.value), {
            operation: 'round',
            unit,
          });
        },
      },
    },
    ...graphOptions,
  };
};
